import { compose, reject } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { createAction } from 'redux-actions';

import { resetForm, setValue } from '@gef-ui/form-core/stream/transfer/control';

import { appendMode, REQUEST_SIZE } from 'constants/general';
import fetch from 'features/common/fetch';
import { pushToFilterDataLayer } from 'features/common/gtm/utils';
import { getQuickFilterState, getUnlockChannelDescById } from 'features/views/unlockedClients/selectors';
import userContext from 'features/views/userContext';
import { generateCommonFetchActions, trimFormData } from 'utils';

import * as types from './actionTypes';
import { formDataFieldsMap, quickFilter, UNLOCK_METHODS } from './constants';
import { FIELDS, UNLOCKED_CLIENTS_FORM_NAME } from './form';
import { requestBodyFactory } from './utils';

const url = fetch.constants.urls.unlockedClientsList;
const processFormData = compose(reject(isNilOrEmpty), trimFormData);

export const changeQuickFilter = createAction(types.CHANGE_QUICK_FILTER, (type) => ({ type }));

export const reset = createAction(types.RESET);
// eslint-disable-next-line max-len
export const appendUnlockedClients = createAction(types.APPEND_UNLOCKED_CLIENTS, (unlockedClients) => ({
	unlockedClients,
}));
export const setUnlockedClients = createAction(types.SET_UNLOCKED_CLIENTS, (unlockedClients) => ({ unlockedClients }));

export const resetFormCriteria =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		dispatch(reset());
		dispatch(fetch.actions.clearFetchStore(url));
		sendControlNext(UNLOCKED_CLIENTS_FORM_NAME, resetForm());
	};

export const loadWithQuickFilter =
	(type) =>
	(dispatch, getState, { sendControlNext }) => {
		dispatch(changeQuickFilter(type));

		let unlockChannel;
		switch (type) {
			case quickFilter.MOBILE_APPLICATION:
				unlockChannel = UNLOCK_METHODS.mobileApplication;
				break;
			case quickFilter.SMS:
				unlockChannel = UNLOCK_METHODS.sms;
				break;
			case quickFilter.WEBCAM:
				unlockChannel = UNLOCK_METHODS.webcam;
				break;
			case quickFilter.PLANNED_ACTIVITY:
				unlockChannel = UNLOCK_METHODS.plannedActivity;
				break;
			case quickFilter.BANK_ID:
				unlockChannel = UNLOCK_METHODS.bankId;
				break;
			case quickFilter.E_DOKLADY:
				unlockChannel = UNLOCK_METHODS.eDoklady;
				break;
		}

		sendControlNext(UNLOCKED_CLIENTS_FORM_NAME, resetForm());
		sendControlNext(
			UNLOCKED_CLIENTS_FORM_NAME,
			setValue({ fieldName: FIELDS.unlockChannels, value: [unlockChannel] }),
		);

		dispatch(fetchUnlockedClients());
	};

export const fetchUnlockedClients =
	(page = 0) =>
	async (dispatch, getState, { getFormData }) => {
		const data = await getFormData(UNLOCKED_CLIENTS_FORM_NAME);
		const formData = processFormData(data);
		const body = requestBodyFactory(formData);
		const queryParams = { size: REQUEST_SIZE, page };
		dispatch(load({ body, queryParams, callGtmFilter: true }, appendMode.SET));
	};

export const load =
	({ body, queryParams, callGtmFilter = false }, append) =>
	(dispatch, getState) => {
		const contextUser = userContext.selectors.getContextUser(getState());
		const quickFilterType = getQuickFilterState(getState());
		let finalFormData;

		dispatch(
			fetch.actions.post(url, {
				takeLatest: true,
				queryParams,
				additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
				body,
				onSuccess: [
					(response) => () => {
						dispatch(formatData(response?.body, append));

						if (callGtmFilter) {
							if (quickFilterType) {
								finalFormData = {};
								switch (quickFilterType) {
									case quickFilter.MOBILE_APPLICATION:
										finalFormData.quickFilterMobileApp = true;
										break;
									case quickFilter.PLANNED_ACTIVITY:
										finalFormData.quickFilterPlannedActivity = true;
										break;
									case quickFilter.SMS:
										finalFormData.quickFilterSms = true;
										break;
									case quickFilter.WEBCAM:
										finalFormData.quickFilterWebcam = true;
										break;
									case quickFilter.BANK_ID:
										finalFormData.bankId = true;
										break;
								}
							} else {
								const cleansedFormData = trimFormData(body);
								finalFormData = { ...cleansedFormData };
							}

							pushToFilterDataLayer({
								filterSection: 'Odemčení klienti',
								formData: finalFormData,
								formDataFieldsMap,
							});
						}
					},
				],
			}),
		);
	};

export const { loadSorted, loadMore, loadPage, reload } = generateCommonFetchActions(load, url);

// adds shortDescription to unlockChannels prop
export const formatData = (body, append) => (dispatch, getState) => {
	const clients = body.data;

	const formattedClients = clients.map((client) => {
		const unlockChannel = getUnlockChannelDescById(getState(), client.unlockChannel);

		return {
			...client,
			unlockChannel,
		};
	});

	if (append === appendMode.APPEND) {
		dispatch(appendUnlockedClients(formattedClients));
	} else {
		dispatch(setUnlockedClients(formattedClients));
	}
};
